import { useCallback, useEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { TaskId } from 'api/models';
import { Action, EventType, ObjectType, SocketData } from 'api/pushes';
import { TaskAPI } from 'api/tasks';
import { FetchStatus } from 'data/core';
import { Task } from 'data/tasks/index';
import { debouncedFetchTaskSaga } from 'hooks/use-debounced-fetch-task';
import { useProjectWSUpdates } from 'hooks/use-project-ws-updates';
import { useTaskDetailQuery } from 'queries/task';
import { saveTaskDetailSaga } from '../actions';
import { selectTask } from '../selectors';
import { deleteTask, saveTasks } from '../slice';

/**
 *
 * @deprecated Use `TasksStore.ofId`. Additional information like attachments
 * should come from other stores.
 */
export const useTaskDetailStore = (taskId: TaskId) => {
  const [, projectId] = taskId;
  const dispatch = useDispatch();

  const task = useSelector(selectTask(taskId), shallowEqual);

  const { data, isLoading, ...rest } = useTaskDetailQuery(taskId);
  const { error } = rest;

  useEffect(() => {
    const isDeleted = error?.response?.status === 403;

    if (isDeleted) {
      dispatch(deleteTask(taskId));
    } else if (!isLoading && data) {
      dispatch(saveTaskDetailSaga(data));
    }
  }, [dispatch, data, isLoading, error, taskId]);

  const handleSocketUpdate = useCallback(
    async (socketData: SocketData<Task | number[]>) => {
      if (Array.isArray(socketData.data)) {
        if (socketData.data.length > 0) {
          const newTask = await TaskAPI.getTask(
            socketData.projectId,
            socketData.data[0],
          );

          if (newTask.parentTask) {
            const parentTask = await TaskAPI.getTask(
              socketData.projectId,
              newTask.parentTask,
            );

            dispatch(
              saveTasks({
                data: [parentTask],
                fetchStatus: FetchStatus.Loaded,
              }),
            );
          }
        }

        return;
      }
      const {
        data: { taskId: taskIdReceivedFromSocket },
      } = socketData;
      const [, , taskIdFromParam] = taskId;

      if (taskIdReceivedFromSocket === taskIdFromParam) {
        debouncedFetchTaskSaga(
          TaskId(socketData.projectId, taskIdReceivedFromSocket),
          dispatch,
        );
      }
    },
    [taskId, dispatch],
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdate,
    EventType(Action.Update, ObjectType.Task),
  );

  useProjectWSUpdates(
    projectId,
    handleSocketUpdate,
    EventType(Action.Duplicate, ObjectType.Task),
  );

  return { data: task, isLoading, ...rest };
};
